import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Printer } from 'lucide-react'

import type { PrintJobStatus } from '@/lib/api/print-admin-api'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { AgentStatusCard } from '@/components/print-admin/agent-status-card'
import { PrintJobsTable } from '@/components/print-admin/print-jobs-table'
import { useAuth } from '@/hooks/use-auth'
import {
    usePrintAgents,
    usePrintJobs,
    useRetryPrintJob,
    useTestPrint,
} from '@/hooks/use-print-admin-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/settings/printing',
)({
    component: PrintingPage,
})

const STATUS_FILTERS: PrintJobStatus[] = [
    'queued',
    'printing',
    'printed',
    'failed',
]

const STATUS_LABELS: Record<PrintJobStatus, () => string> = {
    queued: () => m.print_admin_status_queued(),
    printing: () => m.print_admin_status_printing(),
    printed: () => m.print_admin_status_printed(),
    failed: () => m.print_admin_status_failed(),
}

/**
 * NGOẠI LỆ có chủ đích: màn này KHÔNG dùng `ListFilterBar`.
 *
 * Trang có hai mục — Agent và Lệnh in — còn ô chọn dưới đây chỉ lọc mục Lệnh
 * in, nên nó nằm ngay tiêu đề mục đó. Dựng một thanh lọc cấp trang ở đây là
 * nói với người dùng rằng cả trang đang bị lọc, trong khi phần Agent thì không.
 *
 * Đừng "sửa cho đồng bộ" với các màn danh sách khác.
 */
function PrintingPage() {
    const { hasPermission } = useAuth()
    const [statusFilter, setStatusFilter] = useState<PrintJobStatus | ''>('')
    const [page, setPage] = useState(1)
    const [testDialogOpen, setTestDialogOpen] = useState(false)

    // Queries stay disabled for users without the permission so a direct URL
    // visit renders the denial message without firing doomed 403 calls.
    const canManage = hasPermission('print.manage')
    const { data: overview, isError: agentsError } = usePrintAgents({
        enabled: canManage,
    })
    const {
        data: jobsData,
        isLoading,
        isError: jobsError,
    } = usePrintJobs(
        {
            status: statusFilter || undefined,
            page,
            per_page: 20,
        },
        { enabled: canManage },
    )
    const retryMutation = useRetryPrintJob()
    const testMutation = useTestPrint()

    if (!canManage) {
        return (
            <div className="py-12 text-center text-muted-foreground">
                {m.common_permission_denied()}
            </div>
        )
    }

    const jobs = jobsData?.data ?? []
    const meta = jobsData?.meta

    return (
        <div className="space-y-6 page-pad">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <div>
                        <h1 className="text-xl font-semibold">
                            {m.print_admin_title()}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {m.print_admin_description()}
                        </p>
                    </div>
                </div>
                <Button size="sm" onClick={() => setTestDialogOpen(true)}>
                    <Printer className="size-4" />
                    {m.print_admin_test_print()}
                </Button>
            </div>

            {agentsError || jobsError ? (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                    {m.print_admin_load_error()}
                </div>
            ) : null}

            {/* Agents */}
            <section className="space-y-2">
                <div className="flex items-center gap-2">
                    <h2 className="font-medium">
                        {m.print_admin_agents_title()}
                    </h2>
                    <Badge variant="secondary">
                        {m.print_admin_queued_jobs({
                            count: String(overview?.queued_jobs ?? 0),
                        })}
                    </Badge>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {(overview?.agents ?? []).map((agent) => (
                        <AgentStatusCard key={agent.id} agent={agent} />
                    ))}
                </div>
            </section>

            {/* Jobs */}
            <section className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="font-medium">
                        {m.print_admin_jobs_title()}
                    </h2>
                    <Select
                        value={statusFilter || 'all'}
                        onValueChange={(v) => {
                            setStatusFilter(
                                v === 'all' ? '' : (v as PrintJobStatus),
                            )
                            setPage(1)
                        }}
                    >
                        <SelectTrigger className="w-44">
                            <SelectValue
                                placeholder={m.print_admin_filter_all()}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                {m.print_admin_filter_all()}
                            </SelectItem>
                            {STATUS_FILTERS.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {STATUS_LABELS[s]()}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <PrintJobsTable
                    jobs={jobs}
                    isLoading={isLoading}
                    onRetry={(id) => retryMutation.mutate(id)}
                    retryPending={retryMutation.isPending}
                />

                {meta && meta.total_pages > 1 && (
                    <div className="flex items-center justify-end gap-2 text-sm">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage((p) => p - 1)}
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="text-muted-foreground">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                )}
            </section>

            {/* Test print confirm */}
            <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.print_admin_test_print_confirm_title()}
                        </DialogTitle>
                        <DialogDescription>
                            {m.print_admin_test_print_confirm_desc()}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setTestDialogOpen(false)}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            disabled={testMutation.isPending}
                            onClick={() => {
                                testMutation.mutate(undefined, {
                                    onSettled: () => setTestDialogOpen(false),
                                })
                            }}
                        >
                            {m.common_confirm()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
